import type {ReactNode} from 'react';

import {Navigate, useLocation} from 'react-router-dom';
import {sanitizeRedirect} from 'utils/Helper';

import {routes} from './routes';

const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  const isVendor = localStorage.getItem('isVendor');

  if (!token || !refreshToken) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('isVendor');
    return {authenticated: false, isVendor: false};
  }
  return {authenticated: true, isVendor};
};

const publicPaths = [routes.LOGIN, routes.USER_REGISTER];
const vendorOnlyPaths = [
  routes.HOME,
  routes.PROFILE,
  routes.ORDERS,
  routes.ORDER_DETAILS,
  routes.PROMOTION_CENTER,
  routes.PROMOTION_CENTER_INFO,
  routes.PROMOTION_CENTER_CAMPAIGN,
  routes.PROMOTION_CENTER_DISCOUNT_VIEW,
  routes.PROMOTION_CENTER_PIN_LIST,
  routes.PROMOTION_CENTER_PIN_DETAILS,
  routes.PROMOTION_CENTER_PIN_BUY_PACKAGE,
  routes.PROMOTION_CENTER_PIN_BASKET,
  routes.PROMOTION_CENTER_PIN_PAYMENT_SUCCESS,
  routes.SERVICES_AREA,
  routes.TICKETS,
  routes.TICKET_DETAILS,
  routes.NEW_TICKET,
  routes.VENDOR_SCORE,
  routes.CHORTKE,
  routes.CHORTKE_UNFAIR_PRICE,
  routes.CHORTKE_SHOW_ITEM
];
export const userOnlyPaths = [routes.VENDOR_REGISTER, routes.VENDOR_REGISTER_OVERVIEW];

type ProtectedRouteProps = {
  children: ReactNode;
};

const ProtectedRoute = ({children}: ProtectedRouteProps) => {
  const location = useLocation();
  const {authenticated, isVendor} = isAuthenticated();
  const isPublicPath = publicPaths.includes(location.pathname);
  const isVendorPath = vendorOnlyPaths.some((path) => location.pathname.startsWith(path));
  const isUserPath = userOnlyPaths.includes(location.pathname);

  // Not authenticated and trying to access protected route
  if (!authenticated && !isPublicPath && !isUserPath) {
    const rawPath = location.pathname + location.search;
    const safePath = sanitizeRedirect(rawPath, routes.HOME);
    const redirect = encodeURIComponent(safePath);
    return <Navigate to={`${routes.LOGIN}?redirect=${redirect}`} replace />;
  }

  // Authenticated and trying to access login/register — honour redirect param if present
  if (authenticated && isPublicPath) {
    return <Navigate to={isVendor ? routes.HOME : routes.VENDOR_REGISTER} replace />;
  }

  // User trying to access user-only paths (vendor registration)
  if (!authenticated && isUserPath) {
    const rawPath = location.pathname + location.search;
    const safePath = sanitizeRedirect(rawPath, routes.VENDOR_REGISTER);
    const redirect = encodeURIComponent(safePath);
    return <Navigate to={`${routes.USER_REGISTER}?redirect=${redirect}`} replace />;
  }

  // Normal user trying to access vendor-only paths
  if (!isVendor && isVendorPath) {
    return <Navigate to={routes.VENDOR_REGISTER} replace />;
  }

  return children;
};

export default ProtectedRoute;
